"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRoutePathname } from "@/hooks/use-route-pathname";
import { formatAppSlug, parseAppIdFromSlug, shortAppName } from "@/lib/utils";
import { getCategoryNameById } from "@/lib/constants";
import { formatCategorySlug } from "@/lib/category-utils";
import { designCategory } from "@/lib/firstparty/design-categories";
import { countryCode } from "@/lib/security/input";
import type { AskEvent } from "@/lib/ask/agent";
import { ASK_STORAGE_KEY } from "./hand-off";

export type AskApp = {
  id: number;
  name: string;
  developer: string | null;
  icon: string | null;
  rank: number | null;
  prevRank: number | null;
  rating: number | null;
  ratings: number | null;
  revenue: number | null;
  downloads: number | null;
  category: string | null;
  country: string | null;
  released: string | null;
  /** One more fact the tool knew, e.g. "312 new ads" or "12 days at #1". */
  extra: string | null;
  href: string;
};
export type AskStep = { id: string; name: string; label: string; args: Record<string, unknown>; done: boolean; ok?: boolean };
export type AskResult = { id: string; name: string; args: Record<string, unknown>; ok: boolean; data: unknown };
export type AskMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  steps: AskStep[];
  results: AskResult[];
  status: "streaming" | "done" | "error";
  error?: string;
  /** An answer's id, sent with the question and with a thumbs up or down on it. */
  trace?: string;
};
export type AskPageContext = { path: string; title?: string; appId?: number; appName?: string };
export type AskDesign = {
  image: string;
  appId: number;
  appName: string | null;
  flow: string | null;
  screen: string | null;
  pattern: string | null;
  summary: string | null;
  href: string;
};

export const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);

const plural = (n: number, one: string, many: string) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

/** The one fact a site tool adds beyond chart and estimates, worded for a row. */
function extraFact(o: Record<string, unknown>): string | null {
  const n = (k: string) => num(o[k]);
  if (n("new_ads") != null) return plural(n("new_ads")!, "new ad", "new ads") + (o.burst === true ? " · burst" : "");
  if (n("days_at_1") != null) return plural(n("days_at_1")!, "day at #1", "days at #1");
  if (n("countries_at_1") != null) return `#1 in ${plural(n("countries_at_1")!, "country", "countries")}`;
  if (n("places_gained") != null) return `+${n("places_gained")} places`;
  if (n("screen_count") != null) return plural(n("screen_count")!, "screen", "screens");
  return null;
}

/** Every object in a tool result that looks like an app, first seen first. */
export function appsIn(results: AskResult[]): AskApp[] {
  const out = new Map<number, AskApp>();
  const walk = (v: unknown, depth: number) => {
    if (!v || typeof v !== "object" || depth > 4) return;
    if (Array.isArray(v)) return v.forEach((x) => walk(x, depth + 1));
    const o = v as Record<string, unknown>;
    const id = num(o.app_store_id);
    if (id && typeof o.name === "string" && !out.has(id)) {
      out.set(id, {
        id,
        name: o.name,
        developer: typeof o.developer_name === "string" ? o.developer_name : null,
        icon: typeof o.icon_url === "string" ? o.icon_url : null,
        rank: num(o.ranking_position),
        prevRank: num(o.previous_ranking_position),
        rating: num(o.average_rating),
        ratings: num(o.rating_count),
        revenue: num(o.revenue_estimate),
        downloads: num(o.downloads_estimate),
        // get_rankings gives the chart's category, and the overall chart (36) says nothing about the app.
        category: typeof o.category === "string" ? o.category : Number(o.category_id) === 36 ? null : getCategoryNameById(Number(o.category_id)) ?? null,
        country: typeof o.country_code === "string" ? o.country_code : null,
        released: typeof o.release_date === "string" ? o.release_date : null,
        extra: extraFact(o),
        href: `/app/${formatAppSlug(o.name, id)}`,
      });
    }
    for (const x of Object.values(o)) if (x && typeof x === "object") walk(x, depth + 1);
  };
  results.forEach((r) => r.ok && walk(r.data, 0));
  return [...out.values()];
}

const short = (name: string) => shortAppName(name).toLowerCase();

/** An app page opened on a design category scrolls to its "Inside the app" screens. */
const designsQuery = (pattern: unknown) => (typeof pattern === "string" && pattern ? `?cat=${designCategory(pattern)}` : "");

type AskLink = { href: string; label: string };

/** How a find_apps list names its page in the links under an answer. */
function listLabel(list: string, a: Record<string, unknown>, data: Record<string, unknown>): string {
  const cc = countryCode(a.country).toUpperCase();
  const cat = typeof a.category === "string" && a.category !== "Overall" ? a.category : null;
  const labels: Record<string, string> = {
    chart: `${cat ? `${cat} chart` : "Top chart"} · ${cc}`,
    new: "New releases",
    rising: "Rising apps",
    movers: "Top gainers and losers",
    number_ones: `Report · ${String(data.month ?? "")}`,
    search: `Search “${String(a.query ?? "")}”`,
    find: `Find “${String(a.query ?? "")}”`,
    similar: "Compare competitors",
    developer: typeof data.developer === "string" ? data.developer : "Developer",
    advertisers: "Top advertisers",
    designs: "Design library",
  };
  return labels[list] ?? "Open the list";
}

/** The site pages an answer's tool calls came from, so the visitor can open the full view. */
export function linksFor(results: AskResult[]): AskLink[] {
  const out = new Map<string, string>();
  const add = (href: string, label: string) => out.has(href) || out.set(href, label);
  const appName = (id: number) => appsIn(results).find((a) => a.id === id)?.name;
  const appHref = (id: number, country = "us") => `/app/${formatAppSlug(appName(id) ?? "app", id)}${country !== "us" ? `?country=${country}` : ""}`;
  for (const r of results) {
    if (!r.ok) continue;
    const a = r.args;
    const id = num(a.app_id);
    const data = (r.data ?? {}) as Record<string, unknown>;
    switch (r.name) {
      case "find_apps":
        if (typeof data.page === "string") add(data.page, listLabel(String(a.list), a, data));
        break;
      case "get_app_details":
      case "get_reviews":
      case "get_timeline":
      case "get_revenue_history":
        if (id) add(appHref(id, countryCode(a.country)), appName(id) ?? "App page");
        break;
      case "get_ranking_history":
        if (id) add(`/tools/app-ranking-history?app=${id}&country=${countryCode(a.country)}`, `Rank history${appName(id) ? ` · ${appName(id)}` : ""}`);
        break;
      case "get_ads":
        if (id) add(`/advertisers/${formatAppSlug(appName(id) ?? "app", id)}`, `Ads${appName(id) ? ` · ${appName(id)}` : ""}`);
        break;
      case "search_designs":
        add("/designs", "Design library");
        if (id) add(`${appHref(id)}${designsQuery(a.pattern)}`, `${appName(id) ?? "App"} designs`);
        break;
    }
  }
  return [...out].map(([href, label]) => ({ href, label })).slice(0, 4);
}

/** The UTC day of a date a tool returned, "Oct 25" unless told otherwise; null when it is no date. */
export function utcDay(iso: string, o: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" }): string | null {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-US", { ...o, timeZone: "UTC" });
}

/** When an answer's data is from, as its tools dated it: the chart day, the ad week or the report month. */
export function asOfLine(results: AskResult[], apps: AskApp[]): string | null {
  for (const r of results) {
    if (!r.ok) continue;
    const d = (r.data ?? {}) as Record<string, unknown>;
    let line: (string | null | undefined)[] | null = null;
    if (r.name !== "find_apps") continue;
    if (typeof d.chart_day === "string") line = [countryCode(r.args.country).toUpperCase(), typeof r.args.category === "string" && r.args.category !== "Overall" ? r.args.category : null, "Top free", `chart of ${utcDay(d.chart_day)}`];
    else if (typeof d.week === "string") line = [`Ads from the week of ${utcDay(d.week)}`];
    else if (typeof d.month === "string") line = [`Report for ${utcDay(`${d.month}-01`, { month: "long", year: "numeric" })}`];
    if (!line) continue;
    if (apps.some((a) => a.revenue != null || a.downloads != null)) line.push("revenue and downloads are monthly estimates");
    return line.filter(Boolean).join(" · ");
  }
  return null;
}

/** The screens design searches returned, best match first, each once. */
export function designsIn(results: AskResult[]): AskDesign[] {
  const out = new Map<string, AskDesign>();
  for (const r of results) {
    if (!r.ok || r.name !== "search_designs") continue;
    const items = (r.data as { items?: unknown })?.items;
    for (const it of Array.isArray(items) ? items : []) {
      const o = it as Record<string, unknown>;
      const appId = num(o.app_id);
      if (typeof o.image_url !== "string" || !appId || out.has(o.image_url)) continue;
      const appName = typeof o.app_name === "string" ? o.app_name : null;
      out.set(o.image_url, {
        image: o.image_url,
        appId,
        appName,
        flow: typeof o.flow === "string" ? o.flow : null,
        screen: typeof o.screen === "string" ? o.screen : null,
        pattern: typeof o.pattern === "string" ? o.pattern : null,
        summary: typeof o.summary === "string" ? o.summary : null,
        href: `/app/${formatAppSlug(appName ?? "app", appId)}${designsQuery(o.pattern)}`,
      });
    }
  }
  return [...out.values()];
}

/** The apps an answer names, ordered by where it first names them. */
export function appsNamed(text: string, apps: AskApp[]): AskApp[] {
  const t = text.toLowerCase();
  return apps
    .map((a) => ({ a, at: t.indexOf(short(a.name)) }))
    .filter((x) => x.at >= 0 && short(x.a.name).length > 1)
    .sort((x, y) => x.at - y.at)
    .map((x) => x.a);
}

/**
 * An answer opens with "APPS: <id>, <id>" (or "APPS: none"), the apps it is about; the rest is
 * the text to show. While that line is still arriving there is no text yet.
 */
export function splitAnswer(text: string, done = false): { appIds: number[] | null; body: string } {
  const t = text.trimStart();
  if (!done && !t.includes("\n") && "APPS:".startsWith(t.slice(0, 5)) && (t.length < 5 || t.startsWith("APPS:"))) return { appIds: null, body: "" };
  const m = /^APPS:([^\n]*)(?:\n|$)/.exec(t);
  if (!m) return { appIds: null, body: text };
  return { appIds: (m[1].match(/\d+/g) ?? []).map(Number), body: t.slice(m[0].length).trimStart() };
}

/**
 * The apps to show as rows for an answer: the ones it names on its first line, in its order.
 * Until that line arrives, the first ten apps its tools returned stand in for them.
 */
export function answerApps(m: AskMessage): AskApp[] {
  const done = m.status !== "streaming";
  const { appIds, body } = splitAnswer(m.text, done);
  const all = appsIn(m.results);
  if (appIds) return appIds.flatMap((id) => all.find((a) => a.id === id) ?? []);
  if (!done) return designsIn(m.results).length ? [] : all.slice(0, 10);
  const named = appsNamed(body, all);
  return named.length ? named : designsIn(m.results).length ? [] : all;
}

/** The details call of an answer about one app, whose facts read better than a single row. */
export function singleApp(m: AskMessage): AskResult | undefined {
  const facts = m.results.find((r) => r.ok && r.name === "get_app_details");
  return facts && answerApps(m).length <= 1 && appsIn([facts]).length === 1 ? facts : undefined;
}

function usePageContext(): AskPageContext {
  const path = useRoutePathname() ?? "/";
  const [ctx, setCtx] = useState<AskPageContext>({ path });
  useEffect(() => {
    // The page heading settles after navigation; read it once the new page has painted.
    const id = window.setTimeout(() => {
      const appId = path.startsWith("/app/") ? parseAppIdFromSlug(path) : null;
      const title = document.title.split(" | ")[0];
      setCtx({ path, title, appId: appId ?? undefined, appName: appId ? shortAppName(title) : undefined });
    }, 400);
    return () => window.clearTimeout(id);
  }, [path]);
  return ctx;
}

const uid = () => Math.random().toString(36).slice(2, 10);

/** One conversation with /api/ask, shared by the ask bar and /ask, kept for the tab's life in sessionStorage. */
export function useAsk() {
  const page = usePageContext();
  const [messages, setMessages] = useState<AskMessage[]>([]);
  const [busy, setBusy] = useState(false);
  // True once the saved conversation is back, so a page can continue it.
  const [ready, setReady] = useState(false);
  const abort = useRef<AbortController | null>(null);
  const pageRef = useRef(page);
  pageRef.current = page;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(ASK_STORAGE_KEY);
      if (saved) setMessages((JSON.parse(saved) as AskMessage[]).filter((m) => m.status !== "streaming"));
    } catch {}
    setReady(true);
  }, []);
  useEffect(() => {
    if (busy || !ready) return;
    try {
      sessionStorage.setItem(ASK_STORAGE_KEY, JSON.stringify(messages.slice(-20)));
    } catch {}
  }, [messages, busy, ready]);

  const patch = useCallback((id: string, fn: (m: AskMessage) => AskMessage) => {
    setMessages((all) => all.map((m) => (m.id === id ? fn(m) : m)));
  }, []);

  // A request that outlives its component would answer into nothing.
  useEffect(() => () => abort.current?.abort(), []);

  /** Resolves true once the question has an answer or an error, false if it was stopped or never sent. */
  const ask = useCallback(
    async (question: string): Promise<boolean> => {
      const q = question.trim();
      if (!q || abort.current) return false;
      const user: AskMessage = { id: uid(), role: "user", text: q, steps: [], results: [], status: "done" };
      const trace = crypto.randomUUID();
      const reply: AskMessage = { id: uid(), role: "assistant", text: "", steps: [], results: [], status: "streaming", trace };
      const turns = [...messagesRef.current, user]
        .filter((m) => m.status === "done" && m.text)
        .map((m) => ({ role: m.role, content: m.text }));
      setMessages((all) => [...all, user, reply]);
      setBusy(true);
      const ctrl = new AbortController();
      abort.current = ctrl;
      try {
        const res = await fetch("/api/ask", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ turns, page: pageRef.current, trace }),
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || "The assistant could not answer");
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        let finished = false;
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() ?? "";
          for (const line of lines) {
            if (!line.trim()) continue;
            const ev = JSON.parse(line) as AskEvent;
            if (ev.t === "done") finished = true;
            else if (ev.t === "delta") patch(reply.id, (m) => ({ ...m, text: m.text + ev.text }));
            else if (ev.t === "tool")
              patch(reply.id, (m) => ({ ...m, steps: [...m.steps, { id: ev.id, name: ev.name, label: ev.label, args: ev.args, done: false }] }));
            else if (ev.t === "result")
              patch(reply.id, (m) => {
                const step = m.steps.find((s) => s.id === ev.id);
                const results = [...m.results, { id: ev.id, name: ev.name, args: step?.args ?? {}, ok: ev.ok, data: ev.data }];
                return { ...m, steps: m.steps.map((s) => (s.id === ev.id ? { ...s, done: true, ok: ev.ok } : s)), results };
              });
            else if (ev.t === "error") throw new Error(ev.message);
          }
        }
        // A stream cut before its done event (a timeout, a dropped connection) is not an answer.
        if (!finished) throw new Error("The answer was cut off, try again");
        patch(reply.id, (m) => ({ ...m, status: "done" }));
      } catch (e) {
        const aborted = ctrl.signal.aborted;
        patch(reply.id, (m) => ({
          ...m,
          status: aborted && m.text ? "done" : "error",
          error: aborted ? "Stopped" : e instanceof Error ? e.message : "The assistant could not answer",
        }));
      } finally {
        abort.current = null;
        setBusy(false);
      }
      return !ctrl.signal.aborted;
    },
    [patch],
  );

  const stop = useCallback(() => abort.current?.abort(), []);
  const reset = useCallback(() => {
    abort.current?.abort();
    setMessages([]);
  }, []);

  return { messages, ask, busy, stop, reset, page, ready };
}
